/**
 * ExplanationBanner Component
 *
 * Post-game banner panel revealed when logos part fully.
 * Shows animated pale-gold paillette background with four charcoal rectangles
 * containing column category/explanation text.
 *
 * Layout (board coordinates):
 *   Container: 964 × 164 (charcoal interior within brown banner border)
 *   4 charcoal rects: 208 × 132, sharp corners
 *   X positions: 9, 255, 501, 747 (centered over columns)
 */

import { useMemo } from 'react';
import { useGameContext } from '../contexts/GameContext';
import { fitRecapCategoryText } from '../utils/textFitting';

const BANNER_W = 964;
const BANNER_H = 164;
const RECT_W = 208;
const RECT_H = 132;
const RECT_TOP = 16;
const RECT_X = [9, 255, 501, 747];
const CHARCOAL_BG = '#262626';
const CREAM = '#f7e5c5';

interface ExplanationBannerProps {
  style?: React.CSSProperties;
}

const CategoryRect: React.FC<{ text: string; x: number }> = ({ text, x }) => {
  const fit = useMemo(
    () => fitRecapCategoryText(text, RECT_W),
    [text]
  );

  return (
    <div style={{
      position: 'absolute',
      left: x,
      top: RECT_TOP,
      width: RECT_W,
      height: RECT_H,
      backgroundColor: CHARCOAL_BG,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '8px 10px',
      boxSizing: 'border-box',
    }}>
      <span style={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: fit.fontSize,
        color: CREAM,
        lineHeight: 1.3,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
      }}>
        {fit.lines.map((line, i) => (
          <span key={i}>
            {i > 0 && <br />}
            {line}
          </span>
        ))}
      </span>
    </div>
  );
};

const ExplanationBanner: React.FC<ExplanationBannerProps> = ({ style }) => {
  const { puzzle } = useGameContext();
  if (!puzzle) return null;

  const categories = puzzle.categories ?? ['', '', '', ''];

  return (
    <div style={{
      position: 'relative',
      width: BANNER_W,
      height: BANNER_H,
      overflow: 'hidden',
      ...style,
    }}>
      {/* Solid cream background */}
      <div style={{ position: 'absolute', width: BANNER_W, height: BANNER_H, backgroundColor: CREAM }} />

      {/* Four category explanation rectangles */}
      {RECT_X.map((x: number, i: number) => (
        <CategoryRect key={i} text={categories[i]} x={x} />
      ))}
    </div>
  );
};

export default ExplanationBanner;
